import React, { useEffect, useState } from "react";
import { getCiRateDetail } from "../../api/competitiveIntelligence";
import { fmtOcc, categoryLabel } from "../../utils/ciFormat";
import { MONTHS, fmtRate } from "./shared";
import InfoTip from "../shared/InfoTip";
import ThinScroll from "../shared/ThinScroll";
import { SkeletonText } from "../shared/Skeleton";
import { ErrorState } from "../shared/StateMessage";
import useDrawerDialog from "../../hooks/useDrawerDialog";

// "2026-05-01" → "May 1, 2026". Parses as a plain date string to avoid UTC timezone shift.
const fmtFullDate = (iso) => {
  if (!iso) return "—";
  const [year, month, day] = iso.split("-");
  return `${MONTHS[parseInt(month, 10) - 1]} ${parseInt(day, 10)}, ${year}`;
};

const fmtGapDollars = (value) => {
  if (value == null) return "—";
  const sign = value > 0 ? "+" : value < 0 ? "−" : "";
  return `${sign}$${Math.abs(Number(value)).toFixed(2)}`;
};

const fmtGapPct = (value) => {
  if (value == null) return "—";
  const sign = value > 0 ? "+" : "";
  return `${sign}${Number(value).toFixed(1)}%`;
};

// Positive client_gap_pct means the client charges MORE than this competitor — keep parity with RateCalendarPanel.
// Spec's Opportunity Score scale: Blue >+10% (premium), Green +5% to +10% (at-market positive),
// Neutral ±5%, Red <-5% (below market).
const gapTone = (value) => {
  if (value == null) return "neutral";
  if (value > 10) return "premium";
  if (value > 5) return "good";
  if (value < -5) return "bad";
  return "neutral";
};

const GAP_TONE_CLASS = {
  premium: "text-blue-600 dark:text-blue-400",
  good: "text-emerald-600 dark:text-emerald-400",
  bad: "text-red-600 dark:text-red-400",
  neutral: "text-gray-600 dark:text-gray-300",
};

const fmtDistance = (value) => (value == null ? "—" : `${Number(value).toFixed(1)} mi`);

// Occupancy unavailability reasons (Brian E4). When inferred_occupancy_pct is null
// the API now returns a reason code; we map each to a plain-language tooltip shown
// on the muted "—" badge. Unknown / missing reasons fall back to a generic note.
// Colours stay neutral (gray) — never error/warning — so a data gap doesn't read
// as an alert.
const OCC_FALLBACK_TEXT = "Occupancy isn't available for this stay.";
const COMPETITOR_OCC_REASON_TEXT = {
  source_reports_no_site_counts: "This park doesn't publish how many sites are open.",
  no_capacity_baseline: "Not enough history yet to estimate occupancy.",
  availability_unknown: "Availability wasn't listed for this stay.",
  not_comcomputed: "Not available for this stay.",
};
// Client occupancy reuses the same reason set and adds its own date-rate reason.
const CLIENT_OCC_REASON_TEXT = {
  ...COMPETITOR_OCC_REASON_TEXT,
  no_client_rate_for_date: "You have no rate listed for this stay.",
};
const occReasonText = (reason, map) => (reason && map[reason]) || OCC_FALLBACK_TEXT;

// Renders an occupancy value, or a muted "—" with an InfoTip explaining *why* it's
// missing when the value is null. `tip` is the resolved reason text (or fallback).
const OccupancyValue = ({ value, tip }) => {
  if (value != null) {
    return <span title="Estimated from booking platform inventory data.">{fmtOcc(value)}</span>;
  }
  return (
    <span className="inline-flex items-center text-gray-400 dark:text-gray-500">
      —
      <InfoTip text={tip} />
    </span>
  );
};

const fmtDataAge = (iso) => {
  if (!iso) return "—";
  const timestamp = new Date(iso).getTime();
  if (Number.isNaN(timestamp)) return "—";
  const days = Math.floor((Date.now() - timestamp) / 86400000);
  if (days <= 0) return "today";
  return `${days}d ago`;
};

const ageTone = (iso) => {
  if (!iso) return "fresh";
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return days >= 14 ? "stale" : "fresh";
};

// Spec rule: never say "sold out" — always frame absence as limited availability.
const fmtAvailability = (status) => {
  if (!status) return "—";
  if (status === "sold_out") return "Showing limited availability";
  if (status === "available") return "Available";
  return status.replace(/_/g, " ");
};

// Tier-coded dot in the property cell. Colors follow the spec tier palette:
// Direct=emerald #10B981, Hotel=blue #3B82F6, Airbnb=pink #EC4899.
const TIER_META = {
  direct: { label: "Direct", dotClass: "bg-emerald-500", btnClass: "border-emerald-500 text-emerald-700 dark:text-emerald-300" },
  hotel: { label: "Hotel", dotClass: "bg-blue-500", btnClass: "border-blue-500 text-blue-600 dark:text-blue-300" },
  airbnb: { label: "Airbnb", dotClass: "bg-pink-500", btnClass: "border-pink-500 text-pink-600 dark:text-pink-300" },
};

const TierDot = ({ tier }) => {
  const meta = TIER_META[tier] ?? { dotClass: "bg-gray-400" };
  return <span className={`inline-block w-2.5 h-2.5 rounded-full shrink-0 ${meta.dotClass}`} />;
};

const TIER_TABS = [
  { value: null, label: "All" },
  { value: "direct", label: "Direct" },
  { value: "hotel", label: "Hotel" },
  { value: "airbnb", label: "Airbnb" },
];

const initialState = { loading: false, error: "", data: null };

const RateDetailDrawer = ({ open, onClose, clientId, checkIn, categoryCode, dateWindowName }) => {
  const [tier, setTier] = useState(null);
  const [state, setState] = useState(initialState);

  // Reset tier whenever the drawer opens for a new row.
  useEffect(() => {
    if (open) setTier(null);
  }, [open, checkIn, categoryCode]);

  useEffect(() => {
    if (!open || !clientId || !checkIn || !categoryCode) return;
    let cancelled = false;
    setState({ loading: true, error: "", data: null });
    getCiRateDetail(clientId, { checkIn, categoryCode, tier })
      .then((resp) => {
        if (cancelled) return;
        const rows = resp?.data ?? [];
        setState({ loading: false, error: "", data: rows });
      })
      .catch((err) => {
        if (!cancelled) setState({ loading: false, error: err?.message || "Failed to load rate detail", data: null });
      });
    return () => { cancelled = true; };
  }, [open, clientId, checkIn, categoryCode, tier]);

  // Owns Escape, the focus trap, initial/restored focus, and the `inert` +
  // aria-hidden pair while closed (this drawer previously set inert itself via a
  // JSX-prop spread; the hook now applies it imperatively so it works on React 18
  // and 19 alike).
  const { containerRef, closeButtonRef } = useDrawerDialog(open, onClose);

  const rows = Array.isArray(state.data) ? state.data : [];
  // Client-side rate is identical across rows (it's the campground's own rate for this date) — pull from any row.
  const clientRow = rows.find((row) => row.client_rate != null) || rows[0] || null;

  const headerLeft = dateWindowName || fmtFullDate(checkIn);
  const headerRight = categoryLabel(categoryCode);

  return (
    <>
      <div
        onClick={onClose}
        aria-hidden="true"
        className={`fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
      />

      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-label="Rate detail"
        className={`fixed z-50 bg-white dark:bg-gray-900 shadow-2xl flex flex-col transition-transform duration-300 ease-in-out
          inset-x-0 bottom-0 top-0 h-full
          md:inset-y-0 md:right-0 md:left-auto md:top-0 md:w-[860px] md:max-w-[calc(100vw-60px)]
          ${open ? "translate-y-0 md:translate-x-0" : "translate-y-full md:translate-y-0 md:translate-x-full"}`}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800 shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                {headerLeft}
                {headerRight && <span className="text-gray-400 dark:text-gray-500"> — {headerRight}</span>}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                Check-in {fmtFullDate(checkIn)}
                {clientRow?.nights ? ` · ${clientRow.nights} night${clientRow.nights === 1 ? "" : "s"}` : ""}
              </p>
            </div>
            <button
              ref={closeButtonRef}
              type="button"
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Tier filter */}
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Tier:</span>
            {TIER_TABS.map((tab) => {
              const active = tier === tab.value;
              return (
                <button
                  key={tab.value ?? "__all__"}
                  type="button"
                  onClick={() => setTier(tab.value)}
                  className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                    active
                      ? "border-[#32B1FF] bg-[#32B1FF]/10 text-[#32B1FF]"
                      : "border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Your-rate banner */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800 bg-[#32B1FF]/[0.06] dark:bg-[#32B1FF]/[0.10]">
          <div className="flex items-baseline gap-3 flex-wrap">
            <span className="text-xs uppercase tracking-wide font-semibold text-[#32B1FF]">Your Rate</span>
            <span className="text-2xl font-extrabold text-gray-900 dark:text-gray-100">
              {fmtRate(clientRow?.client_rate)}
            </span>
            {clientRow?.client_site_type_name && (
              <span className="text-sm text-gray-700 dark:text-gray-300 truncate" title={clientRow.client_site_type_name}>
                · {clientRow.client_site_type_name}
              </span>
            )}
            <span className="text-sm text-gray-600 dark:text-gray-400 inline-flex items-center">
              ·{" "}
              {clientRow?.client_inferred_occupancy_pct != null ? (
                <span title="Estimated from booking platform inventory data.">
                  {fmtOcc(clientRow.client_inferred_occupancy_pct)} occ
                </span>
              ) : (
                <span className="inline-flex items-center text-gray-400 dark:text-gray-500">
                  — occ
                  <InfoTip
                    text={occReasonText(clientRow?.client_occupancy_unavailable_reason, CLIENT_OCC_REASON_TEXT)}
                  />
                </span>
              )}
            </span>
          </div>
          {clientRow?.client_rate_as_of && (
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Rate as of {fmtFullDate(clientRow.client_rate_as_of.slice(0, 10))}
            </div>
          )}
        </div>

        {/* Body */}
        <ThinScroll className="flex-1 overflow-y-auto">
          {state.loading ? (
            <div className="p-6">
              <SkeletonText lines={5} lineClass="h-12" />
            </div>
          ) : state.error ? (
            <ErrorState variant="inset" padding="p-6" message={state.error} />
          ) : !rows.length ? (
            <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400 space-y-2">
              <div className="font-medium text-gray-700 dark:text-gray-200">
                No {tier ? `${tier} ` : ""}competitor rates for this date
              </div>
              <div className="text-xs">
                {tier ? (
                  <>
                    No <span className="font-medium">{tier}</span> rate data was collected for this date.{" "}
                    <button
                      type="button"
                      onClick={() => setTier(null)}
                      className="text-[#32B1FF] hover:underline font-medium"
                    >
                      Show all tiers
                    </button>
                  </>
                ) : (
                  <>No competitor rate data was collected for this date.</>
                )}
              </div>
            </div>
          ) : (
            <ThinScroll className="overflow-x-auto responsive-card-table-wrapper">
            <table className="w-full min-w-[640px] text-sm responsive-card-table">
              <thead className="text-xs text-gray-500 dark:text-gray-400 sticky top-0 bg-white dark:bg-gray-900">
                <tr className="border-b border-gray-200 dark:border-gray-800">
                  <th className="px-3 py-3 text-left font-medium">Property</th>
                  <th className="px-3 py-3 text-right font-medium">Rate</th>
                  <th className="px-3 py-3 text-right font-medium">Gap</th>
                  <th className="px-3 py-3 text-right font-medium">~Occupancy</th>
                  <th className="px-3 py-3 text-right font-medium">Distance</th>
                  <th className="px-3 py-3 text-right font-medium">Data Age</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => {
                  const tone = gapTone(row.client_gap_pct);
                  const stale = ageTone(row.rate_as_of) === "stale";
                  const availability = fmtAvailability(row.availability_status);
                  return (
                    <tr key={`${row.competitor_atlas_park_id ?? row.competitor_name}-${row.site_type_name}-${index}`}
                        className="border-b dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition">
                      <td data-label="Property" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">
                        <div className="flex items-start gap-2">
                          <span className="mt-1.5"><TierDot tier={row.tier} /></span>
                          <div className="min-w-0">
                            <div className="font-semibold truncate" title={row.competitor_name}>
                              {row.competitor_name}
                            </div>
                            {row.site_type_name && (
                              <div
                                className="text-[12px] text-gray-500 dark:text-gray-400 truncate max-w-[260px]"
                                title={row.site_type_name}
                              >
                                {row.site_type_name}
                              </div>
                            )}
                            {availability && availability !== "Available" && availability !== "—" && (
                              <div className="text-[11px] text-amber-600 dark:text-amber-400 mt-0.5">
                                {availability}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td data-label="Rate" className="px-3 py-2.5 text-right text-gray-900 dark:text-gray-100 whitespace-nowrap">
                        <div className="font-medium">{fmtRate(row.nightly_rate)}</div>
                        {row.normalized_group && (
                          <div className="text-[11px] text-gray-500 dark:text-gray-400">{row.normalized_group}</div>
                        )}
                      </td>
                      <td data-label="Gap" className={`px-3 py-2.5 text-right font-semibold whitespace-nowrap ${GAP_TONE_CLASS[tone]}`}>
                        <div>{fmtGapDollars(row.client_gap_dollars)}</div>
                        <div className="text-[11px] font-normal">{fmtGapPct(row.client_gap_pct)}</div>
                      </td>
                      <td
                        data-label="~Occupancy"
                        className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap"
                      >
                        <OccupancyValue
                          value={row.inferred_occupancy_pct}
                          tip={occReasonText(row.occupancy_unavailable_reason, COMPETITOR_OCC_REASON_TEXT)}
                        />
                      </td>
                      <td data-label="Distance" className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">
                        <div>{fmtDistance(row.competitor_distance_miles)}</div>
                        {row.competitor_rating != null && (
                          <div className="text-[11px] text-gray-500 dark:text-gray-400">
                            ★ {Number(row.competitor_rating).toFixed(1)}
                            {row.competitor_review_count != null && ` (${row.competitor_review_count})`}
                          </div>
                        )}
                      </td>
                      <td
                        data-label="Data Age"
                        className={`px-3 py-2.5 text-right whitespace-nowrap ${
                          stale ? "text-amber-600 dark:text-amber-400" : "text-gray-500 dark:text-gray-400"
                        }`}
                        title={row.rate_as_of || ""}
                      >
                        {fmtDataAge(row.rate_as_of)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </ThinScroll>
          )}
        </ThinScroll>
      </div>
    </>
  );
};

export default RateDetailDrawer;
